import { useCallback, useState, useEffect } from 'react';
import type { Dispatch, SetStateAction } from 'react';

type UseStorageTuple<T> = [T | undefined, Dispatch<SetStateAction<T | undefined>>, () => void];

function useLocalStorage<T>(key: string, _value?: T): UseStorageTuple<T> {
  return useStorage(key, _value, window.localStorage);
}

function useSessionStorage<T>(key: string, _value?: T): UseStorageTuple<T> {
  return useStorage(key, _value, window.sessionStorage);
}

function useStorage<T>(key: string, _value: T, storageObject: Storage): UseStorageTuple<T> {
  const [value, setValue] = useState<T | undefined>(() => {
    const item = storageObject.getItem(key);
    return item ? (parseStoredStoredItem(item) as T) : _value;
  });

  useEffect(
    () => (value ? storageObject.setItem(key, parseNewItem(value)) : storageObject.removeItem(key)),
    [key, value, storageObject],
  );

  const remove = useCallback(() => {
    setValue(undefined);
  }, []);

  return [value, setValue, remove];

  function parseNewItem<U>(item: U | undefined): string {
    return typeof item === 'string' ? item : JSON.stringify(item);
  }

  function parseStoredStoredItem<V>(item: string | null) {
    try {
      return JSON.parse(item || '') as V;
    } catch (error) {
      return item;
    }
  }
}

export default { useLocalStorage, useSessionStorage };
